import { type Report } from '@littlebird/sdk';
import {
  adaptReportToRoutineRun,
  selectRoutineRun,
} from '@littlebird/ui-utils';
import { useRouterState } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';

import { routinesStore } from '../routines.state';

/**
 * Records the run on screen so the header, actions and analytics follow the
 * same one. The selection rule lives in `selectRoutineRun`.
 *
 * Reads MobX state, so callers must be `observer` components.
 */
export function useSelectedRoutineReport(
  routineId: number,
  reports: readonly Report[] | undefined,
  requestedReportId?: number
) {
  const isNavigating = useRouterState({ select: (state) => state.isLoading });
  const selectedReportId = routinesStore.useSelectedReportId(routineId);
  const isReportPinned = routinesStore.useIsReportPinned(routineId);

  const routineReports = useMemo(
    () => reports?.filter((report) => report.reportTemplateId === routineId),
    [reports, routineId]
  );
  const runs = useMemo(
    () =>
      (routineReports ?? []).map((report) => adaptReportToRoutineRun(report)),
    [routineReports]
  );

  const { selectedRun, isPinned } = selectRoutineRun(runs, {
    requestedRunId: requestedReportId,
    pinnedRunId: isReportPinned ? selectedReportId : null,
  });
  const selectedReport = routineReports?.find(
    (report) => report.referenceId === selectedRun?.id
  );

  useEffect(() => {
    // Navigation can still render the previous route’s run in the newly active tab
    if (!routineReports || isNavigating) return;

    const shownReportId = selectedReport?.referenceId ?? null;
    if (shownReportId !== selectedReportId) {
      routinesStore.showReport(routineId, shownReportId, isPinned);
    }
  }, [
    routineId,
    routineReports,
    selectedReport,
    isPinned,
    selectedReportId,
    isNavigating,
  ]);

  return { routineReports, selectedReport };
}
